import { Card, CardContent, CardHeader, CardTitle } from "../../card";
import { FeedList } from "../feed-list";

/**
 * A help-article link list. ONE renderer for both "articles in a category"
 * and "popular articles" — they were two structurally-identical components
 * (`HelpArticleList` + the retired `HelpPopularList`) hand-rolling the same
 * `Card > ul > li > Link` markup. Both now route their rows through the
 * canonical `FeedList`; callers shape their data into `HelpArticleListItem`.
 */
export interface HelpArticleListItem {
  id: number;
  /** Full link target, e.g. `/help/<category>/<slug>`. */
  href: string;
  title: string;
  /** One muted line under the title — article summary OR category name. */
  subtitle?: string;
  /** Optional right-aligned cell — e.g. "12 views". */
  trailing?: string;
}

export interface HelpArticleListProps {
  items: HelpArticleListItem[];
  /** Card header. Omit for a bare, header-less list (e.g. under a PageHeader). */
  title?: string;
  /**
   * Shown in a card when the list is empty. Omit to render nothing when empty
   * (the "popular articles" behaviour — quiet, not an empty box).
   */
  emptyMessage?: string;
}

export function HelpArticleList({ items, title, emptyMessage }: HelpArticleListProps) {
  if (items.length === 0) {
    if (!emptyMessage) return null;
    return (
      <Card>
        <CardContent className="py-8 text-center type-body text-muted-foreground">
          {emptyMessage}
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="gap-0 overflow-hidden py-0">
      {title ? (
        <CardHeader className="border-b border-border px-6 py-2.5">
          <CardTitle className="type-title">{title}</CardTitle>
        </CardHeader>
      ) : null}
      <CardContent className="p-0">
        <FeedList
          rows={items}
          rowKey={(a) => a.id}
          rowHref={(a) => a.href}
          primary={(a) => a.title}
          secondary={(a) => a.subtitle}
          trailing={(a) => a.trailing}
        />
      </CardContent>
    </Card>
  );
}
